/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/PaymentProgressBar/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useEffect, useRef } from "react";
import styles from "./PaymentProgressBar.module.css";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/PaymentModal/CheckmarkIcon.svg";

const PaymentProgressBar = ({ steps, currentStep }) => {
  const stepperRef = useRef(null);

  useEffect(() => {
    if (stepperRef.current) {
      stepperRef.current.style.setProperty("--steps-count", steps.length);
    }
  }, [steps.length]);

  const getProgressWidth = () => {
    if (currentStep === 1) {
      return 25;
    } else if (currentStep === 2) {
      return 75;
    } else {
      return 100;
    }
  };

  const isStepCompleted = (index) => {
    if (currentStep === 1) {
      return false;
    } else if (currentStep === 2) {
      return index === 0;
    } else if (currentStep === 3) {
      return index < currentStep; // Mark all previous steps as completed when on step 3
    } else {
      return index < currentStep;
    }
  };

  return (
    <div className={styles.stepperContainer}>
      <div className={styles.stepper} ref={stepperRef}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressBarFill}
            style={{ width: `${getProgressWidth()}%` }}
          ></div>
        </div>

        {steps.map((step, index) => (
          <div
            key={index}
            className={`${styles.step} ${
              isStepCompleted(index) ? styles.stepCompleted : ""
            }`}
          >
            <div className={styles.stepLabel}>{step.label}</div>
            <div className={styles.stepCircle}>
              <CheckmarkIcon />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentProgressBar;
